import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Loader2, FileText, Download, LogOut, Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { billingRequestCode, billingVerifyCode, billingListInvoices } from "@/lib/api";

const STORAGE_KEY = "hac_billing_token";
const EMAIL_KEY = "hac_billing_email";

/**
 * Advertiser self-service billing portal.
 * - Magic-code login: email → 6-digit OTP → 30-day JWT in localStorage.
 * - Lists all invoices for that billing_email (descending).
 * - Shows an upsell strip with the latest active discount code.
 */
const BillingPortalPage = () => {
  const { lang } = useLang();
  const isNl = lang === "nl";
  const [token, setToken] = useState(() => (typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null));
  const [email, setEmail] = useState(() => (typeof window !== "undefined" ? localStorage.getItem(EMAIL_KEY) || "" : ""));
  const [step, setStep] = useState(token ? "logged-in" : "email");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [data, setData] = useState({ items: [], upsell: null });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (token && step === "logged-in") {
      setLoading(true);
      billingListInvoices(token)
        .then(setData)
        .catch(() => {
          localStorage.removeItem(STORAGE_KEY);
          setToken(null);
          setStep("email");
          setError(isNl ? "Sessie verlopen — log opnieuw in." : "Session expired — please log in again.");
        })
        .finally(() => setLoading(false));
    }
  }, [token, step, isNl]);

  const requestCode = async (e) => {
    e.preventDefault();
    setError(""); setInfo("");
    setBusy(true);
    try {
      await billingRequestCode(email.trim().toLowerCase());
      setStep("code");
      setInfo(isNl ? `Code verzonden naar ${email}. Controleer ook je spam-folder.` : `Code sent to ${email}. Check your spam folder too.`);
    } catch (err) {
      setError(err?.response?.data?.detail || (isNl ? "Versturen mislukt" : "Failed to send code"));
    } finally { setBusy(false); }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { token: tk } = await billingVerifyCode(email.trim().toLowerCase(), code.trim());
      localStorage.setItem(STORAGE_KEY, tk);
      localStorage.setItem(EMAIL_KEY, email.trim().toLowerCase());
      setToken(tk);
      setStep("logged-in");
      setInfo("");
    } catch (err) {
      setError(err?.response?.data?.detail || (isNl ? "Code ongeldig of verlopen" : "Code invalid or expired"));
    } finally { setBusy(false); }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(EMAIL_KEY);
    setToken(null); setEmail(""); setCode("");
    setStep("email"); setData({ items: [], upsell: null });
  };

  if (step === "logged-in" && token) {
    return (
      <main className="bg-[#F8FAFC] min-h-screen pb-16" data-testid="billing-portal-loggedin">
        <section className="hac-container py-12 max-w-4xl">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <div className="overline mb-2">{isNl ? "Mijn facturen" : "My invoices"}</div>
              <h1 className="font-heading text-3xl md:text-4xl text-[#0F172A]">{email}</h1>
            </div>
            <button onClick={logout} className="text-sm text-[#64748B] hover:text-[#0F172A] inline-flex items-center gap-1" data-testid="billing-logout"><LogOut size={14} /> {isNl ? "Uitloggen" : "Log out"}</button>
          </div>

          {data.upsell && (
            <div className="bg-gradient-to-br from-violet-600 to-indigo-700 text-white rounded-2xl p-6 mb-6" data-testid="billing-upsell">
              <div className="flex items-center gap-2 mb-2"><Sparkles size={16} /> <span className="text-xs uppercase tracking-widest font-bold opacity-90">{isNl ? "Bonus voor terugkerende adverteerders" : "Bonus for returning advertisers"}</span></div>
              <h2 className="font-heading text-2xl mb-2">{isNl ? `Verleng of upgrade je advertentie met ${data.upsell.percent_off}% korting` : `Renew or upgrade with ${data.upsell.percent_off}% off`}</h2>
              <p className="text-sm text-white/85 mb-4">{data.upsell.description || (isNl ? "Gebruik onderstaande code bij je volgende advertentie-submission." : "Use the code below on your next ad submission.")}</p>
              <div className="flex items-center gap-3 flex-wrap">
                <code className="bg-white/15 border border-white/30 text-white px-3 py-1.5 rounded-lg font-mono text-base font-bold tracking-widest" data-testid="billing-upsell-code">{data.upsell.code}</code>
                <Link to={`/advert-submit?code=${encodeURIComponent(data.upsell.code)}`} className="bg-white text-violet-700 hover:bg-violet-50 font-bold text-sm px-4 py-2 rounded-full inline-flex items-center gap-1.5" data-testid="billing-upsell-cta">{isNl ? "Plaats nieuwe advertentie" : "Place a new ad"} <ArrowRight size={13} /></Link>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-12 text-[#94A3B8]"><Loader2 size={20} className="animate-spin inline" /></div>
          ) : data.items.length === 0 ? (
            <div className="text-center py-16 bg-white border border-dashed border-[#CBD5E1] rounded-2xl">
              <FileText size={32} className="mx-auto text-[#94A3B8] mb-2" />
              <p className="text-sm text-[#64748B] mb-3">{isNl ? "Nog geen facturen op dit emailadres." : "No invoices yet on this email."}</p>
              <Link to="/sponsor" className="hac-btn-primary">{isNl ? "Maak je eerste advertentie" : "Create your first ad"} <ArrowRight size={13} /></Link>
            </div>
          ) : (
            <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden" data-testid="billing-invoices-list">
              <table className="w-full text-sm">
                <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                  <tr className="text-left text-[10px] uppercase tracking-widest text-[#64748B]">
                    <th className="px-4 py-3">{isNl ? "Nummer" : "Number"}</th>
                    <th className="px-4 py-3">{isNl ? "Datum" : "Date"}</th>
                    <th className="px-4 py-3">{isNl ? "Bedrijf" : "Company"}</th>
                    <th className="px-4 py-3 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((inv) => (
                    <tr key={inv.number} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]" data-testid={`billing-invoice-${inv.number}`}>
                      <td className="px-4 py-3 font-mono text-[#0F172A]">{inv.number}</td>
                      <td className="px-4 py-3 text-[#64748B]">{new Date(inv.created_at).toLocaleDateString(isNl ? "nl-NL" : "en-US", { day: "numeric", month: "short", year: "numeric" })}</td>
                      <td className="px-4 py-3 text-[#0F172A]">{inv.company}</td>
                      <td className="px-4 py-3 text-right">
                        {inv.url ? (
                          <a href={inv.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-[#2563EB] hover:underline" data-testid={`billing-invoice-download-${inv.number}`}><Download size={12} /> PDF</a>
                        ) : (
                          <span className="text-xs text-[#94A3B8] italic">{isNl ? "niet beschikbaar" : "unavailable"}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    );
  }

  return (
    <main className="bg-[#F8FAFC] min-h-screen pb-16" data-testid="billing-portal-login">
      <section className="hac-container py-16 max-w-md">
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8">
          <div className="inline-flex items-center gap-2 bg-[#0F172A] text-white px-3 py-1 rounded-full text-xs font-bold mb-4">
            <Mail size={12} /> {isNl ? "Advertentie-portaal" : "Advertiser portal"}
          </div>
          <h1 className="font-heading text-3xl text-[#0F172A] leading-tight mb-2">
            {isNl ? "Mijn facturen" : "My invoices"}
          </h1>
          <p className="text-sm text-[#64748B] mb-6">
            {isNl
              ? "Log in met je facturatie-emailadres om al je HorseAppCompare facturen te bekijken en downloaden."
              : "Log in with your billing email to view and download all your HorseAppCompare invoices."}
          </p>
          {step === "email" && (
            <form onSubmit={requestCode} className="space-y-3">
              <div>
                <label className="overline block mb-1">{isNl ? "Email" : "Email"}</label>
                <input type="email" className="hac-input" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="finance@yourcompany.com" data-testid="billing-email-input" />
              </div>
              <button type="submit" disabled={busy || !email} className="hac-btn-primary w-full justify-center" data-testid="billing-request-code">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} {isNl ? "Stuur login code" : "Send login code"}
              </button>
            </form>
          )}
          {step === "code" && (
            <form onSubmit={verifyCode} className="space-y-3">
              <div>
                <label className="overline block mb-1">{isNl ? "Code uit je email" : "Code from your email"}</label>
                <input className="hac-input text-center font-mono text-lg tracking-widest" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} required maxLength={6} placeholder="123456" data-testid="billing-code-input" autoFocus />
                <p className="text-[11px] text-[#94A3B8] mt-1">{isNl ? "6-cijferige code, geldig 15 minuten." : "6-digit code, valid for 15 minutes."}</p>
              </div>
              <button type="submit" disabled={busy || code.length !== 6} className="hac-btn-primary w-full justify-center" data-testid="billing-verify-code">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} {isNl ? "Log in" : "Log in"}
              </button>
              <button type="button" onClick={() => { setStep("email"); setCode(""); setError(""); setInfo(""); }} className="text-xs text-[#64748B] hover:text-[#0F172A] w-full">
                ← {isNl ? "Andere email gebruiken" : "Use different email"}
              </button>
            </form>
          )}
          {info && <p className="mt-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2" data-testid="billing-info">{info}</p>}
          {error && <p className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="billing-error">{error}</p>}
        </div>
        <p className="text-[11px] text-[#94A3B8] text-center mt-4">{isNl ? "Geen advertenties? " : "No ads yet? "}<Link to="/sponsor" className="text-[#2563EB] hover:underline">{isNl ? "Begin op /sponsor →" : "Start on /sponsor →"}</Link></p>
      </section>
    </main>
  );
};

export default BillingPortalPage;
